import React, {useEffect, useState} from "react"
import {useCookies} from "react-cookie"
import {useToasts} from "react-toast-notifications"
import {AuthError, Token, useIsAuthenticated, useWebauthnAuthentication} from "../../services/auth"
import LoginContainer from "./LoginContainer"
import Button from "../../components/Button/Button"
import {useLocation, useNavigate} from "react-router"
import {AsyncResultIdleRequestState} from "../../types/asyncResultIdle"

function Error({
	startAuthState,
	finishAuthState,
}: {
	startAuthState: AsyncResultIdleRequestState<string, AuthError>
	finishAuthState: AsyncResultIdleRequestState<Token, AuthError>
}) {
	const isErrorOnStart = startAuthState.match(
		() => false,
		() => false,
		(_) => false,
		(_) => true
	)

	const isErrorOnFinish = finishAuthState.match(
		() => false,
		() => false,
		(_) => false,
		(_) => true
	)

	return isErrorOnStart || isErrorOnFinish ? (
		<p className="help is-danger">Failed to authenticate. Try again or use a password.</p>
	) : null
}

export default function LoginBiometric() {
	const navigate = useNavigate()
	const {addToast} = useToasts()
	const [username, setUsername] = useState("")
	const location = useLocation()
	const {from} = location.state || {from: {pathname: "/"}}
	const [cookies, , removeCookie] = useCookies(["login_failure"])
	const isAuthenticated = useIsAuthenticated()

	const [startAuthState, startAuthenticate, finishAuthState, finishAuthenticate] = useWebauthnAuthentication()

	useEffect(
		() =>
			finishAuthState.iter(() => {
				return navigate(from, {replace: true})
			}),
		[finishAuthState]
	)

	useEffect(() => {
		try {
			const errorCookie = cookies.login_failure
			if (errorCookie) addToast(errorCookie.reason, {appearance: "error"})
		} finally {
			removeCookie("login_failure")
		}
	}, [])

	useEffect(() => {
		isAuthenticated ? navigate(from, {replace: true}) : null
	}, [isAuthenticated])

	async function startAuthentication(username: string) {
		const result = await startAuthenticate(username)
		if (result.isErr()) {
			return result.error
		}
		const token = await finishAuthenticate(username, result.value)
		if (token.isErr()) {
			return token.error
		}
	}

	return (
		<LoginContainer>
			<div className="login-container is-flex is-flex-direction-column is-align-self-center is-justify-content-center">
				<span className="label login-title login-sso">Biometric Sign-in</span>
				<form
					className="login-form"
					onSubmit={(e) => {
						e.preventDefault()
						startAuthentication(username)
					}}
				>
					<div className="field">
						<label className="label">Email Address</label>
						<div className="control">
							<input
								className="input is-medium"
								type="text"
								value={username}
								placeholder="your@email.com"
								onChange={(e) => setUsername(e.target.value)}
								required
								autoComplete="on"
							/>
						</div>
						<Error startAuthState={startAuthState} finishAuthState={finishAuthState} />
					</div>
					<div className="field login-buttons control is-flex is-align-items-center">
						<div className="alternative-login-buttons">
							<Button className="login-button mr-3" theme="neutral" rounded>
								Continue
							</Button>
							<Button
								onClick={() => navigate("/login")}
								className="login-button"
								theme="neutral"
								variant="outline"
								rounded
							>
								Back To Sign in
							</Button>
						</div>
					</div>
				</form>
			</div>
		</LoginContainer>
	)
}
